import React, { useState, useEffect, useCallback } from "react";
import {
  Container, Header, SpaceBetween, Box, Button,
} from "@cloudscape-design/components";
import { UserInfo, MaintenanceEvent, ShiftTypeConfig } from "../types";
import { apiClient } from "../api/client";
import { EventModal } from "../components/EventModal";
import { BulkAddModal } from "../components/BulkAddModal";

interface Props { user: UserInfo; siteId: string; }

const DEFAULT_SHIFT_TYPES: ShiftTypeConfig[] = [
  { id: "day", name: "Day Shift", color: "#FF9900" },
  { id: "night", name: "Night Shift", color: "#0972D3" },
  { id: "morning", name: "Morning Window", color: "#7B2F8E" },
];

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

export const MaintenanceSchedule: React.FC<Props> = ({ user, siteId }) => {
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [events, setEvents] = useState<MaintenanceEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<MaintenanceEvent | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");

  // Site config - trades and shift types
  const [availableTrades, setAvailableTrades] = useState<string[]>([]);
  const [shiftTypes, setShiftTypes] = useState<ShiftTypeConfig[]>(DEFAULT_SHIFT_TYPES);

  const isAdmin = user.role === "site-admin" || user.role === "superadmin";
  const monthStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}`;

  // Fetch site config for trades + shift types
  const fetchSiteConfig = useCallback(async () => {
    try {
      const data = await apiClient.get(`/sites?siteId=${siteId}`);
      const site = data.site;
      if (site) {
        setAvailableTrades(site.trades || []);
        setShiftTypes(site.shiftTypes && site.shiftTypes.length > 0 ? site.shiftTypes : DEFAULT_SHIFT_TYPES);
      }
    } catch (err) {
      console.error("Failed to fetch site config:", err);
    }
  }, [siteId]);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get(`/events?siteId=${siteId}&month=${monthStr}`);
      setEvents(data.events || []);
    } catch (err) {
      console.error("Failed to fetch events:", err);
    } finally {
      setLoading(false);
    }
  }, [siteId, monthStr]);

  useEffect(() => { fetchSiteConfig(); }, [fetchSiteConfig]);
  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // Helper: get shift color by ID
  const getShiftColor = (shiftId: string) => {
    const st = shiftTypes.find(s => s.id === shiftId);
    return st?.color || "#888";
  };

  // Helper: get shift background (lighter) from color
  const getShiftBg = (shiftId: string) => {
    const color = getShiftColor(shiftId);
    // Convert hex to rgba with low opacity
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, 0.08)`;
  };

  const changeMonth = (delta: number) => {
    let m = currentMonth + delta;
    let y = currentYear;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setCurrentMonth(m);
    setCurrentYear(y);
  };

  const handleCellClick = (dateStr: string) => {
    if (!isAdmin) return;
    setSelectedDate(dateStr);
    setSelectedEvent(null);
    setShowEventModal(true);
  };

  const handleEventClick = (ev: MaintenanceEvent) => {
    setSelectedEvent(ev);
    setSelectedDate(ev.date);
    setShowEventModal(true);
  };

  // Build calendar grid
  const firstDay = new Date(currentYear, currentMonth, 1).getDay();
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const totalCells = Math.ceil((firstDay + daysInMonth) / 7) * 7;

  const calendarCells = [];
  for (let i = 0; i < totalCells; i++) {
    const dayNum = i - firstDay + 1;
    let dateStr = "";
    let display = "";
    let isOther = false;

    if (dayNum >= 1 && dayNum <= daysInMonth) {
      display = String(dayNum);
      dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
    } else {
      isOther = true;
    }

    const dayEvents = dateStr
      ? events
          .filter(e => e.date === dateStr)
          .sort((a, b) => {
            const aIdx = shiftTypes.findIndex(s => s.id === a.shift);
            const bIdx = shiftTypes.findIndex(s => s.id === b.shift);
            return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
          })
      : [];
    calendarCells.push({ dayNum, dateStr, display, isOther, events: dayEvents });
  }

  return (
    <SpaceBetween size="l">
      <Container header={
        <Header
          variant="h1"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {isAdmin && <Button onClick={() => setShowBulkModal(true)}>Bulk Add</Button>}
              {isAdmin && <Button variant="primary" onClick={() => { setSelectedEvent(null); setSelectedDate(""); setShowEventModal(true); }}>Add Event</Button>}
            </SpaceBetween>
          }
        >
          Maintenance Schedule
        </Header>
      }>
        {/* Legend - dynamic from shift types */}
        <SpaceBetween direction="horizontal" size="m">
          {shiftTypes.map(st => (
            <Box key={st.id}>
              <span style={{ display: "inline-block", width: 12, height: 12, borderRadius: 2, background: st.color, marginRight: 6 }}></span>
              <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>{st.name}</span>
            </Box>
          ))}
        </SpaceBetween>
      </Container>

      {/* Calendar Navigation */}
      <Container>
        <SpaceBetween direction="horizontal" size="m">
          <Button iconName="angle-left" variant="icon" onClick={() => changeMonth(-1)} />
          <Box variant="h2">{MONTHS[currentMonth]} {currentYear}</Box>
          <Button iconName="angle-right" variant="icon" onClick={() => changeMonth(1)} />
          <Button onClick={() => { const t = new Date(); setCurrentYear(t.getFullYear()); setCurrentMonth(t.getMonth()); }}>Today</Button>
        </SpaceBetween>

        {/* Calendar Grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", border: "1px solid #d5d9d9", borderRadius: 8, overflow: "hidden", marginTop: 16 }}>
          {DAYS.map(d => (
            <div key={d} style={{ padding: "8px 4px", textAlign: "center", fontSize: "0.78rem", fontWeight: 700, background: "#f3f3f3", borderBottom: "1px solid #d5d9d9", textTransform: "uppercase" }}>
              {d}
            </div>
          ))}
          {calendarCells.map((cell, idx) => (
            <div
              key={idx}
              onClick={() => cell.dateStr && handleCellClick(cell.dateStr)}
              style={{
                minHeight: 100,
                padding: 4,
                borderRight: (idx + 1) % 7 !== 0 ? "1px solid #e7e7e7" : undefined,
                borderBottom: "1px solid #e7e7e7",
                background: cell.isOther ? "#fafafa" : "white",
                cursor: isAdmin && cell.dateStr ? "pointer" : "default",
              }}
            >
              <div style={{ fontSize: "0.8rem", fontWeight: 600, color: "#888", padding: "2px 4px" }}>{cell.display}</div>
              {cell.events.map(ev => (
                <div
                  key={ev.eventId}
                  onClick={(e) => { e.stopPropagation(); handleEventClick(ev); }}
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    padding: "2px 5px",
                    marginBottom: 2,
                    borderRadius: 3,
                    borderLeft: `3px solid ${getShiftColor(ev.shift)}`,
                    background: getShiftBg(ev.shift),
                    color: getShiftColor(ev.shift),
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {ev.title}
                  {ev.trades && ev.trades.length > 0 && (
                    <span style={{ marginLeft: 4, opacity: 0.7, fontSize: "0.6rem" }}>
                      [{ev.trades.join(", ")}]
                    </span>
                  )}
                  {ev.commentCount && ev.commentCount > 0 && (
                    <span style={{
                      marginLeft: 4, display: "inline-flex", alignItems: "center", justifyContent: "center",
                      width: 16, height: 16, borderRadius: "50%", background: "#545b64", color: "#fff",
                      fontSize: "0.55rem", fontWeight: 700, verticalAlign: "middle",
                    }}>
                      {ev.commentCount}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </Container>

      {/* Event Modal */}
      {showEventModal && (
        <EventModal
          visible={showEventModal}
          onDismiss={() => setShowEventModal(false)}
          onSave={fetchEvents}
          event={selectedEvent}
          defaultDate={selectedDate}
          siteId={siteId}
          user={user}
          isAdmin={isAdmin}
          availableTrades={availableTrades}
          shiftTypes={shiftTypes}
        />
      )}

      {/* Bulk Add Modal */}
      {showBulkModal && (
        <BulkAddModal
          visible={showBulkModal}
          onDismiss={() => setShowBulkModal(false)}
          onSave={fetchEvents}
          siteId={siteId}
          availableTrades={availableTrades}
          shiftTypes={shiftTypes}
        />
      )}
    </SpaceBetween>
  );
};
